import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { matchPath, useHistory, useLocation, useParams } from "react-router-dom";

import Breadcrumbs from "../../../../components/BreadCrumbs";
import {
  ReportView,
  ResolveReportModal,
  buildEventScopedReportBreadcrumbs,
  mapLogisticsIssueComments,
  mapReportDetailToViewModel,
} from "../../../../components/Events/Event/Reports/ReportView";
import useReportViewLabels from "../../../../components/Events/Event/Reports/ReportView/useReportViewLabels";
import { REPORT_ISSUER_TYPES } from "../../../../components/Events/Event/Reports/reportMappers";
import messages from "../../../../assets/locale/messages";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  canAddCompanyModule,
  canEditCompanyModule,
} from "../../../../utils/Permissions";
import { EVENT_REPORT_URL_PARAM } from "../../../../utils/eventViewTabs";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { buildIssueImagesAttributes } from "../../../../components/Events/Event/Reports/reportUpload";
import {
  createCompanyLogisticsIssueCommentRequest,
  getCompanyLogisticsIssueCommentsRequest,
  getCompanyLogisticsIssueDetailRequest,
  updateCompanyLogisticsIssueRequest,
} from "../../../../store/CompanyPortal/LogisticsIssues/actions";

import "../Reports.scss";

const resolveCompanyReportId = (pathname, search, routeId) => {
  const fromSearch = new URLSearchParams(search).get(EVENT_REPORT_URL_PARAM);
  if (fromSearch) return fromSearch;

  const isStandaloneReportRoute = Boolean(
    matchPath(pathname, {
      path: ROUTE_PATHS.companyViewReport,
      exact: true,
    }),
  );

  return isStandaloneReportRoute ? routeId : null;
};

const CompanyViewReport = () => {
  const { id: routeId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const id = resolveCompanyReportId(
    location.pathname,
    location.search,
    routeId,
  );
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const { report, shared, events } = messages[lang];
  const detail = useSelector((state) => state.companyLogisticsIssues.detail);
  const comments = useSelector(
    (state) => state.companyLogisticsIssues.comments,
  );
  const updating = useSelector(
    (state) => state.companyLogisticsIssues.updating,
  );
  const commenting = useSelector(
    (state) => state.companyLogisticsIssues.commenting,
  );
  const labels = useReportViewLabels(report, shared);
  const [resolveOpen, setResolveOpen] = useState(false);

  const canCommentReports = canAddCompanyModule(
    companyPermissionModules.reports,
    login,
  );
  const canResolveReports = canEditCompanyModule(
    companyPermissionModules.reports,
    login,
  );

  useEffect(() => {
    if (!id) return;
    dispatch(getCompanyLogisticsIssueDetailRequest({ id }));
    dispatch(
      getCompanyLogisticsIssueCommentsRequest({
        id,
        params: { page: -1 },
      }),
    );
  }, [dispatch, id, lang]);

  const viewModel = useMemo(() => {
    const base = mapReportDetailToViewModel(detail, {
      lang,
      labels: labels.view,
    });
    if (!base) return null;

    const mappedComments = mapLogisticsIssueComments(comments);
    return {
      ...base,
      comments: mappedComments.length ? mappedComments : base.comments,
    };
  }, [comments, detail, lang, labels.view]);

  const eventId = detail?.event?.id || detail?.event_id;

  const breadcrumbs = useMemo(() => {
    const mappedTitle =
      viewModel?.title && viewModel.title !== "-" ? viewModel.title : null;
    const reportTitle =
      (detail?.title && String(detail.title).trim()) ||
      mappedTitle ||
      report.listReportBreadcrumb;

    if (!eventId) {
      return [
        {
          path: ROUTE_PATHS.companyReports,
          label: shared.reports,
        },
        {
          path: "",
          label: reportTitle,
        },
      ];
    }

    const eventViewPath = ROUTE_PATHS.companyViewEvent.replace(":id", eventId);
    const eventName =
      detail?.event?.name ||
      detail?.event_name ||
      viewModel?.eventName ||
      "";

    return buildEventScopedReportBreadcrumbs({
      eventsListPath: ROUTE_PATHS.companyEvents,
      eventViewPath,
      eventStatus: detail?.event?.status || detail?.event_status,
      eventName,
      reportIdLabel: reportTitle,
      reportCrumbLabel: report.viewReport.breadcrumbReport,
      eventsMessages: events,
    });
  }, [
    detail,
    eventId,
    events,
    report.listReportBreadcrumb,
    report.viewReport.breadcrumbReport,
    shared.reports,
    viewModel,
  ]);

  const handleResolveConfirm = ({ comment, imageUrl, imageUrls } = {}) => {
    if (!id) return;
    const urls = Array.isArray(imageUrls)
      ? imageUrls.filter(Boolean)
      : imageUrl
        ? [imageUrl]
        : [];
    const images = buildIssueImagesAttributes(urls);
    dispatch(
      updateCompanyLogisticsIssueRequest({
        id,
        data: {
          status: "closed",
          ...(comment
            ? { comment, resolution_description: comment }
            : {}),
          ...(images.length
            ? { logistics_issue_images_attributes: images }
            : {}),
        },
        onSuccess: () => {
          setResolveOpen(false);
          dispatch(getCompanyLogisticsIssueDetailRequest({ id }));
        },
      }),
    );
  };

  const handlePostComment = (description) => {
    if (!id || !description) return;
    dispatch(
      createCompanyLogisticsIssueCommentRequest({
        id,
        data: { description },
        onSuccess: () => {
          dispatch(
            getCompanyLogisticsIssueCommentsRequest({
              id,
              params: { page: -1 },
            }),
          );
        },
      }),
    );
  };

  const handleViewProfile = () => {
    const reporterId = viewModel?.reporterId;
    if (!reporterId) return;

    if (viewModel?.reporterType === REPORT_ISSUER_TYPES.employee) {
      const isOwnAccount = String(login?.id) === String(reporterId);
      history.push(
        isOwnAccount
          ? ROUTE_PATHS.companySettingsProfile
          : ROUTE_PATHS.companyTeamEmployeeView.replace(":id", reporterId),
      );
      return;
    }

    // Supervisor + Yoram Admin → applicant profile
    history.push(
      ROUTE_PATHS.companyViewApplicantProfile.replace(":id", reporterId),
    );
  };

  const canViewReporterProfile = Boolean(viewModel?.reporterId);

  return (
    <div className="company-reports">
      <div className="page-title-margin">
        <Breadcrumbs list={breadcrumbs} BreadCrumbHasBackIcon={false} />
      </div>

      {viewModel ? (
        <ReportView
          report={viewModel}
          labels={labels.view}
          canResolve={canResolveReports && viewModel.isOpen}
          canComment={canCommentReports && viewModel.isOpen}
          commenting={commenting}
          onResolve={() => setResolveOpen(true)}
          onPostComment={handlePostComment}
          onViewProfile={canViewReporterProfile ? handleViewProfile : undefined}
        />
      ) : null}

      <ResolveReportModal
        open={resolveOpen}
        labels={labels.resolve}
        submitting={updating}
        onClose={() => setResolveOpen(false)}
        onConfirm={handleResolveConfirm}
      />
    </div>
  );
};

export default CompanyViewReport;
